'use client';

import { c as _c } from "react/compiler-runtime";
import { Card } from '@/components/Card';
import { ProjectLogo } from '@/components/ProjectLogo';
import { TechChip } from '@/components/TechChip';
import { GitHubIcon } from '@/components/SocialIcons';
import { AppStoreIcon, ChangelogIcon } from '@/components/icons';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useExternalLinkTracking } from '@/hooks/useExternalLinkTracking';

// Max tech chips shown on a card before collapsing into a "+N" pill.
const MAX_CHIPS = 5;

// Icons
function LinkIcon(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M15.71 11.82a.75.75 0 1 0 1.06 1.06l-1.06-1.06Zm-4.95 1.77a.75.75 0 0 0 1.06-1.06l-1.06 1.06Zm-2.48-1.41a.75.75 0 1 0-1.06-1.06l1.06 1.06Zm4.95-1.77a.75.75 0 1 0-1.06 1.06l1.06-1.06Zm3.36.53-.884.88 1.06 1.06.885-.883-1.06-1.06Zm-4.95-2.12 1.41-1.42L12 6.34l-1.42 1.41 1.06 1.06Zm0 3.54a2.5 2.5 0 0 1 0-3.54l-1.06-1.06a4 4 0 0 0 0 5.66l1.06-1.06Zm4.95-4.95a2.5 2.5 0 0 1 0 3.54L17.66 12a4 4 0 0 0 0-5.66l-1.06 1.06Zm1.06-1.06a4 4 0 0 0-5.66 0l1.06 1.06a2.5 2.5 0 0 1 3.54 0l1.06-1.06Zm-7.07 7.07.176.18 1.06-1.06-.176-.177-1.06 1.06Zm-3.18-.353.88-.884-1.06-1.06-.884.88 1.06 1.06Zm4.95 2.12-1.41 1.41 1.06 1.06 1.42-1.41-1.06-1.06Zm0-3.54a2.5 2.5 0 0 1 0 3.54l1.06 1.06a4 4 0 0 0 0-5.66l-1.06 1.06Zm-4.95 4.95a2.5 2.5 0 0 1 0-3.54L6.34 12a4 4 0 0 0 0 5.66l1.06-1.06Zm-1.06 1.06a4 4 0 0 0 5.66 0l-1.06-1.06a2.5 2.5 0 0 1-3.54 0l-1.06 1.06Zm7.07-7.07-.176-.177-1.06 1.06.176.18 1.06-1.06Z" fill="currentColor" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function YoutubeIcon(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M21.58,6.19c-0.23-0.86-0.91-1.54-1.77-1.77C18.25,4,12,4,12,4S5.75,4,4.19,4.42 c-0.86,0.23-1.54,0.91-1.77,1.77C2,7.75,2,12,2,12s0,4.25,0.42,5.81c0.23,0.86,0.91,1.54,1.77,1.77 C5.75,20,12,20,12,20s6.25,0,7.81-0.42c0.86-0.23,1.54-0.91,1.77-1.77C22,16.25,22,12,22,12S22,7.75,21.58,6.19z M10,15.46V8.54L16,12L10,15.46z" fill="currentColor" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

// Icon map for additional links
const ICON_MAP = {
  GitHubIcon,
  AppStoreIcon,
  ChangelogIcon,
  YoutubeIcon
} as const;

// Serializable project data (no functions)
export interface ProjectData {
  name: string;
  description: string;
  // Optional: internal tools (e.g. Dallas) have no public live link.
  mainLink?: {
    href: string;
    label: string;
  };
  additionalLinks?: Array<{
    href: string;
    label: string;
    iconName: keyof typeof ICON_MAP;
  }>;
  // Optional: teaser/internal projects fall back to a monogram.
  logoSrc?: string;
  logoScale?: number;
  detailPage?: string;
  // Techs without a matching logo render as text-only chips (techLogos resolves
  // the display name directly, so no separate logoKey is needed here).
  techStack?: Array<{
    name: string;
  }>;
}
interface ProjectCardProps {
  project: ProjectData;
}
export function ProjectCard(t0) {
  const $ = _c(58);
  const {
    project
  } = t0;
  const {
    track
  } = useAnalytics();
  const trackExternalLink = useExternalLinkTracking();
  let t1;
  if ($[0] !== project.name || $[1] !== track) {
    t1 = () => {
      track("project_view", {
        project_name: project.name,
        source: "projects_page"
      });
    };
    $[0] = project.name;
    $[1] = track;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleProjectClick = t1;
  let t2;
  if ($[3] !== project.name || $[4] !== track) {
    t2 = (e, techName) => {
      e.stopPropagation();
      track("tech_stack_interest", {
        technology: techName,
        project: project.name,
        location: "projects_list"
      });
    };
    $[3] = project.name;
    $[4] = track;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleTechClick = t2;
  let t3;
  if ($[6] !== project.mainLink || $[7] !== project.name || $[8] !== trackExternalLink) {
    t3 = e_0 => {
      e_0.stopPropagation();
      if (project.mainLink) {
        trackExternalLink(project.mainLink.href, `project_${project.name}`);
      }
    };
    $[6] = project.mainLink;
    $[7] = project.name;
    $[8] = trackExternalLink;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleMainLinkClick = t3;
  let t4;
  if ($[10] !== project.name || $[11] !== trackExternalLink) {
    t4 = (e_1, href, label) => {
      e_1.stopPropagation();
      const labelLower = label.toLowerCase();
      const type = labelLower.includes("github") ? "github" : labelLower.includes("app store") ? "appstore" : labelLower.includes("changelog") ? "changelog" : labelLower.includes("demo") ? "demo" : "other";
      trackExternalLink(href, `project_${project.name}`, type);
    };
    $[10] = project.name;
    $[11] = trackExternalLink;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handleAdditionalLinkClick = t4;
  let T0;
  let t10;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[13] !== handleProjectClick || $[14] !== handleTechClick || $[15] !== project.description || $[16] !== project.detailPage || $[17] !== project.logoScale || $[18] !== project.logoSrc || $[19] !== project.mainLink?.href || $[20] !== project.name || $[21] !== project.techStack) {
    const chips = project.techStack ?? [];
    const visibleChips = chips.slice(0, MAX_CHIPS);
    const overflow = chips.length - visibleChips.length;
    const detailHref = project.detailPage || project.mainLink?.href || "#";
    T0 = Card;
    t5 = "li";
    t6 = "h-full";
    if ($[29] !== project.logoScale || $[30] !== project.logoSrc || $[31] !== project.name) {
      t7 = <ProjectLogo src={project.logoSrc} alt={project.name} size={12} logoScale={project.logoScale} />;
      $[29] = project.logoScale;
      $[30] = project.logoSrc;
      $[31] = project.name;
      $[32] = t7;
    } else {
      t7 = $[32];
    }
    if ($[33] !== detailHref || $[34] !== handleProjectClick || $[35] !== project.name) {
      t8 = <h2 className="project-title mt-6 text-base font-semibold text-zinc-800 dark:text-zinc-100"><Card.Link href={detailHref} onClick={handleProjectClick}>{project.name}</Card.Link></h2>;
      $[33] = detailHref;
      $[34] = handleProjectClick;
      $[35] = project.name;
      $[36] = t8;
    } else {
      t8 = $[36];
    }
    if ($[37] !== project.description) {
      t9 = <Card.Description>{project.description}</Card.Description>;
      $[37] = project.description;
      $[38] = t9;
    } else {
      t9 = $[38];
    }
    t10 = visibleChips.length > 0 && <div className="relative z-20 mt-4 flex flex-wrap gap-2">{visibleChips.map(tech => <TechChip key={tech.name} className="flex cursor-pointer items-center rounded-full bg-zinc-100 px-3 py-1 text-xs font-medium text-zinc-700 group-hover:bg-zinc-200 dark:bg-zinc-800/80 dark:text-zinc-300 dark:group-hover:bg-zinc-700" label={tech.name} logoName={tech.name} logoSize={16} logoClassName="mr-1.5 size-4" onClick={e_2 => handleTechClick(e_2, tech.name)} />)}{overflow > 0 && <span className="flex items-center rounded-full px-2.5 py-1 text-xs font-medium text-zinc-400 dark:text-zinc-500">+{overflow}</span>}</div>;
    $[13] = handleProjectClick;
    $[14] = handleTechClick;
    $[15] = project.description;
    $[16] = project.detailPage;
    $[17] = project.logoScale;
    $[18] = project.logoSrc;
    $[19] = project.mainLink?.href;
    $[20] = project.name;
    $[21] = project.techStack;
    $[22] = T0;
    $[23] = t10;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    T0 = $[22];
    t10 = $[23];
    t5 = $[24];
    t6 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
  }
  let t11;
  if ($[39] !== handleMainLinkClick || $[40] !== project.mainLink) {
    t11 = project.mainLink && <p className="relative z-20 flex items-center text-sm font-medium text-zinc-400 transition group-hover:text-teal-500 dark:text-zinc-200"><LinkIcon className="size-6 flex-none" /><a href={project.mainLink.href} className="ml-2 transition hover:text-teal-500" target="_blank" rel="noopener noreferrer" onClick={handleMainLinkClick}>{project.mainLink.label}</a></p>;
    $[39] = handleMainLinkClick;
    $[40] = project.mainLink;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== handleAdditionalLinkClick || $[43] !== project.additionalLinks || $[44] !== project.name) {
    t12 = project.additionalLinks && project.additionalLinks.length > 0 && <div className="relative z-20 mt-4 flex gap-4">{project.additionalLinks.map(link => {
        const Icon = ICON_MAP[link.iconName];
        return <a key={link.href} href={link.href} className="group/icon relative flex size-8 items-center justify-center rounded-full bg-white/90 shadow-md ring-1 shadow-zinc-800/5 ring-zinc-900/5 transition after:absolute after:-inset-1.5 hover:bg-white dark:border dark:border-zinc-700/50 dark:bg-zinc-800 dark:ring-0 dark:hover:border-zinc-700" title={link.label} aria-label={`${project.name} ${link.label}`} target="_blank" rel="noopener noreferrer" onClick={e_3 => handleAdditionalLinkClick(e_3, link.href, link.label)}><span className="sr-only">{project.name} {link.label}</span><Icon className="size-5 fill-zinc-500 transition group-hover/icon:fill-teal-500 dark:fill-zinc-400 dark:group-hover/icon:fill-teal-400" /></a>;
      })}</div>;
    $[42] = handleAdditionalLinkClick;
    $[43] = project.additionalLinks;
    $[44] = project.name;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] !== t11 || $[47] !== t12) {
    t13 = <div className="mt-auto w-full pt-6">{t11}{t12}</div>;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== T0 || $[50] !== t10 || $[51] !== t13 || $[52] !== t5 || $[53] !== t6 || $[54] !== t7 || $[55] !== t8 || $[56] !== t9) {
    t14 = <T0 as={t5} className={t6}>{t7}{t8}{t9}{t10}{t13}</T0>;
    $[49] = T0;
    $[50] = t10;
    $[51] = t13;
    $[52] = t5;
    $[53] = t6;
    $[54] = t7;
    $[55] = t8;
    $[56] = t9;
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  return t14;
}